import { fetchAPI } from '@/api/fetch';
import { apiUpdateClient } from '@/api/services';
import { apiGetIndustries } from '@/api/services/industry.service';
import {
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogActionTrigger
} from '@/components/ui/dialog';
import { Field } from '@/components/ui/field';
import { Client } from '@/types/Client';
import { Box, Button, Flex, Input, Menu, Portal, Stack } from '@chakra-ui/react';
import { CaretDown, X } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

interface EditClientProfileProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  client: Client;
  onUpdateClient: (client: Client) => void;
}

interface FormValues {
  companyName: string;
  industry: { value: string };
}

const FORM_DEFAULTS: FormValues = {
  companyName: '',
  industry: { value: '' }
};

const ERROR_MESSAGES = {
  FETCH_INDUSTRIES: 'Failed to load industries. Please try again.',
  UPDATE_CLIENT: 'Failed to update client. Please try again.',
  NETWORK_ERROR: 'Network error. Please check your connection and try again.',
  SERVER_ERROR: 'Server error. Please try again later.',
  UNKNOWN_ERROR: 'An unexpected error occurred. Please try again.'
} as const;

export default function EditClientProfile({
  open,
  setOpen,
  client,
  onUpdateClient
}: EditClientProfileProps) {
  const [error, setError] = useState<string | null>(null);
  const [industries, setIndustries] = useState<Industry[]>([]);
  const [isLoadingIndustries, setIsLoadingIndustries] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    formState: { errors, isSubmitting }
  } = useForm<FormValues>({
    defaultValues: FORM_DEFAULTS,
    mode: 'onChange'
  });

  const fetchIndustries = useCallback(async () => {
    if (industries.length > 0 || isLoadingIndustries) return;

    setIsLoadingIndustries(true);
    try {
      const response = await fetchAPI<Industry[]>({
        request: () => apiGetIndustries()
      });

      if (response.success) {
        setIndustries(response.data);
        setError(null);
      } else {
        console.error('Failed to fetch industries:', response.message);
        setError(ERROR_MESSAGES.FETCH_INDUSTRIES);
      }
    } catch (error) {
      console.error('Failed to fetch industries:', error);
      setError(ERROR_MESSAGES.NETWORK_ERROR);
    } finally {
      setIsLoadingIndustries(false);
    }
  }, [industries.length, isLoadingIndustries]);

  useEffect(() => {
    if (open) {
      setValue('companyName', client.name);
      fetchIndustries();
    }
  }, [open, client, setValue, fetchIndustries]);

  useEffect(() => {
    if (open) {
      const industry = industries.find((industry) => industry.name === client.industry);
      if (industry) {
        setValue('industry', { value: industry.name });
      }
    }
  }, [client, industries, setValue, open]);

  const onSubmit = handleSubmit(async (formData) => {
    setError(null);

    try {
      const response = await fetchAPI<void>({
        request: () =>
          apiUpdateClient(client.client_id, {
            name: formData.companyName,
            industry: formData.industry.value
          })
      });

      if (response.success) {
        setOpen(false);
        onUpdateClient({
          ...client,
          name: formData.companyName,
          industry: formData.industry.value
        });
      } else {
        console.error('Failed to update client', response.message);
        setError(ERROR_MESSAGES.UPDATE_CLIENT);
      }
    } catch (error) {
      console.error('Failed to update client:', error);
      setError(ERROR_MESSAGES.NETWORK_ERROR);
    }
  });

  const handleDialogClose = useCallback(() => {
    reset();
    setError(null);
  }, [reset]);

  const isFormDisabled = useMemo(
    () => isSubmitting || isLoadingIndustries,
    [isSubmitting, isLoadingIndustries]
  );

  return (
    <DialogRoot
      open={open}
      onOpenChange={(e) => setOpen(e.open)}
      closeOnInteractOutside={false}
      placement={'center'}
      motionPreset="slide-in-bottom"
      onExitComplete={handleDialogClose}
      size={'lg'}
    >
      <DialogContent borderRadius="12px">
        <form onSubmit={onSubmit} noValidate>
          <DialogHeader>
            <DialogTitle>Edit client profile</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <Box borderBottom="1px solid" borderColor="neutrals.100" />
            <Stack gap="4" my={'20px'}>
              <Field
                label={
                  <Box fontSize="16px">
                    <Box as="span" color="error.500" aria-hidden="true">
                      *{' '}
                    </Box>
                    Company Name
                  </Box>
                }
                invalid={!!errors.companyName}
                errorText={
                  <Box fontSize="14px" color={'error.700'}>
                    {errors.companyName?.message}
                  </Box>
                }
              >
                <Input
                  size="lg"
                  placeholder="Enter company name"
                  {...register('companyName', {
                    required: 'Company name is required',
                    minLength: {
                      value: 2,
                      message: 'Company name must be at least 2 characters'
                    },
                    maxLength: {
                      value: 100,
                      message: 'Company name must not exceed 100 characters'
                    }
                  })}
                  aria-invalid={!!errors.companyName}
                  aria-describedby={errors.companyName ? 'company-name-error' : undefined}
                  disabled={isFormDisabled}
                />
              </Field>
              <Field
                label={
                  <Box fontSize="16px">
                    <Box as="span" color="error.500" aria-hidden="true">
                      *{' '}
                    </Box>
                    Industry
                  </Box>
                }
                invalid={!!errors.industry}
                errorText={
                  <Box fontSize="14px" color="error.700">
                    {errors.industry?.message}
                  </Box>
                }
              >
                <Controller
                  name="industry"
                  control={control}
                  rules={{ required: 'Industry is required' }}
                  render={({ field }) => (
                    <Menu.Root>
                      <Menu.Trigger asChild>
                        <Button
                          variant="outline"
                          size="lg"
                          borderColor={errors.industry ? 'error.500' : 'neutrals.200'}
                          borderRadius="8px"
                          disabled={isFormDisabled}
                          _disabled={{
                            bg: 'neutrals.300'
                          }}
                          w="100%"
                          aria-label="Select industry"
                        >
                          <Flex justifyContent="space-between" alignItems="center" w="100%">
                            <Box
                              fontSize="16px"
                              color="neutrals.700"
                              overflow="hidden"
                              whiteSpace="nowrap"
                              textOverflow="ellipsis"
                            >
                              {field.value?.value || 'Select industry'}
                            </Box>
                            <Box as="span" ml="8px" mr="0">
                              <CaretDown size={16} aria-hidden="true" />
                            </Box>
                          </Flex>
                        </Button>
                      </Menu.Trigger>
                      <Portal>
                        <Menu.Positioner>
                          <Menu.Content
                            minW="10rem"
                            position="fixed"
                            zIndex={1500}
                            maxH="300px"
                            overflowY="auto"
                          >
                            <Menu.RadioItemGroup
                              value={field.value?.value || ''}
                              onValueChange={(value) => {
                                field.onChange({ value });
                              }}
                            >
                              {industries.map(({ industry_id, name }) => (
                                <Menu.RadioItem
                                  key={industry_id}
                                  value={name}
                                  fontSize="16px"
                                  _hover={{
                                    cursor: 'pointer',
                                    bg: 'neutrals.100'
                                  }}
                                >
                                  {name}
                                </Menu.RadioItem>
                              ))}
                            </Menu.RadioItemGroup>
                          </Menu.Content>
                        </Menu.Positioner>
                      </Portal>
                    </Menu.Root>
                  )}
                />
              </Field>
            </Stack>
            <Box borderBottom="1px solid" borderColor="neutrals.100" mb="16px" />
            {error && (
              <Box color="error.700" fontSize="16px" mb="16px" role="alert">
                {error}
              </Box>
            )}
          </DialogBody>
          <DialogFooter>
            <DialogActionTrigger asChild>
              <Button
                w={'120px'}
                borderColor="primary.900"
                background="background.secondary"
                color="primary.900"
                borderRadius="8px"
                fontSize={'16px'}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            </DialogActionTrigger>
            <Button
              type="submit"
              w={'120px'}
              background="primary.900"
              color="neutrals.0"
              borderRadius="8px"
              fontSize={'16px'}
              loading={isSubmitting}
              disabled={isFormDisabled}
            >
              Save
            </Button>
          </DialogFooter>
        </form>
        <DialogCloseTrigger m="12px" disabled={isSubmitting}>
          <X size={32} weight="bold" />
        </DialogCloseTrigger>
      </DialogContent>
    </DialogRoot>
  );
}
